import { flog } from '@lseg-ciam/auth/login';
import { acquireIdpTokens$ } from '@lseg-ciam/auth/token';
import { setVersionContext } from '@lseg-ciam/auth/utils';
import { NEVER, Observable, catchError, switchMap, tap } from 'rxjs';
import { appConfig$ } from './config.js';
import { finalizeLogin, validateRedirectionPaths } from './internal.js';
import {
  defaultAudiencesByClientUid,
  workspaceFinalizeLoginHook,
} from './workspace-config.js';

export function createTeamsLoginStart$(): Observable<never> {
  return appConfig$.pipe(
    validateRedirectionPaths(),
    tap(() => setVersionContext('TEAMS')),
    switchMap(
      ({
        issuerUrl,
        redirectUri,
        nextPath,
        clientUid,
        recoverPath,
        persistTokens,
        idp,
        userId,
        lane,
        pnsSub,
      }) => {
        if (!clientUid) {
          throw new Error('Missing clientUid query param.');
        }

        // Teams flow is auto-detected by acquireIdpTokens$ when the issuer config
        // returns clientType: 'teams'. The strategy here doesn't matter much since
        // Teams uses its own native token acquisition via MS Teams SDK.
        return acquireIdpTokens$({
          issuerUrl,
          redirectUri,
          clientUid,
          issuerConfigOverride: {
            idps: {
              entra: {
                well_known: 'UNUSED',
                client_id: 'IMPLICIT',
                swap: 'id_token',
              },
            },
            authorization_endpoint: '',
            token_endpoint: `${issuerUrl}/token`,
            scopes_supported: [],
            client_type: 'teams',
          },
          idp,
          userId,
          lane,
          pnsSub,
          preferred: { strategy: 'silent' },
          fallback: { strategy: 'none' },
          optimistic: false,
          defaultAudiencesByClientUid,
        }).pipe(
          flog('Teams login idp bundle'),
          finalizeLogin({
            nextPath,
            persistTokens,
            onFinalizeLogin: workspaceFinalizeLoginHook,
          }),
          // finalize is effects-only; this journey owns navigation
          tap(() => {
            if (nextPath) {
              globalThis.location.replace(nextPath);
            }
          }),
          switchMap(() => NEVER),
          catchError((error) => {
            if (!recoverPath) {
              throw error;
            }

            const messageDescription = error.message.split('\n')[0];
            const searchParams = new URLSearchParams({
              error: 'teams_login_error',
              error_description: messageDescription,
            });
            const recoverUrl = new URL(
              `${recoverPath}?${searchParams.toString()}`,
              globalThis.location.origin
            );
            // eslint-disable-next-line no-console
            console.warn(
              `[HEIMDALL] Teams login error, redirecting to recovery page ${recoverUrl}:`,
              error
            );
            globalThis.location.replace(recoverUrl.toString());
            return NEVER;
          })
        );
      }
    ),
    flog('Teams login')
  );
}
